import { useState, useRef, useEffect } from "react";
import { C, API_BASE, getFontFamily } from "../constants/colors";
import { authedFetch } from "../firebase";
import { LANG } from "../constants/lang";
import { askClaude } from "../helpers";
import { StyledTutorText, TypingBubble, Dots, SamajhIcon, LangToggle, RevisionText } from "./ui";
import { SessionFeedback } from "./FeedbackWidget";
import { Confetti } from "./Confetti";
import { RevisionPanel } from "./RevisionPanel";
import { PhotoUpload } from "./PhotoUpload";
import { SPLASH_IMAGES } from "./Splash";
import { SvgCoordinatePlane, SvgPythagoras, SvgTrigRatios, SvgAngleTypes, SvgTriangleTypes, SvgCircleParts, SvgTangentToCircle, SvgDistanceFormula, SvgSectorArea, SvgHeightDistance, SvgParallelTransversal, SvgQuadrilateralTypes, SvgAngleSumProperty, SvgSimilarTriangles, SvgBPT } from "./geometry-slides";
import { SvgReflectionLaw, SvgConcaveMirror, SvgConvexMirror, SvgRefraction, SvgConvexLens, SvgDispersion, SvgHumanEye } from "./light-slides";
import { SvgSoundWave, SvgMagneticFieldLines, SvgSolenoid, SvgElectricMotor, SvgBohrModel, SvgCellStructure, SvgConeSphere } from "./science-slides";
const trackChatQuestionAsked = () => {};
const trackChatSolved = () => {};

// AI-TUTOR-STEP-4-APPLIED: Socratic bottom-sheet overlay shown once per device
function SocraticOverlay({ lang, ff }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    try {
      const seen = localStorage.getItem("samajh_tutor_intro_seen");
      if (!seen) setShow(true);
    } catch {}
  }, []);
  const handleDismiss = () => {
    try { localStorage.setItem("samajh_tutor_intro_seen", "true"); } catch {}
    setShow(false);
  };
  if (!show) return null;

  const isHi = lang === "hi";
  const points = [
    {
      icon: "🙅",
      title: isHi ? "मैं सीधे जवाब नहीं दूँगा" : "I won't just give you the answer",
      sub: isHi ? "असली सीखना ऐसे नहीं होता" : "That's not how real learning works",
    },
    {
      icon: "💡",
      title: isHi ? "मैं सवाल पूछूँगा ताकि तुम खुद समझ सको" : "I'll ask questions that help YOU figure it out",
      sub: isHi ? "step-by-step, तुम्हारी गति से" : "Step by step, at your pace",
    },
    {
      icon: "🎯",
      title: isHi ? "फँसे हो? मैं हिंट दूँगा, solution नहीं" : "Stuck? I'll give hints, not solutions",
      sub: isHi ? "तुम जो सीखोगे वो याद रहेगा" : "You'll actually remember what you learn",
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleDismiss}
        style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.45)", zIndex: 100,
        }}
      />
      {/* Bottom sheet */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "#FFFFFF",
        borderRadius: "24px 24px 0 0",
        padding: "16px 24px 32px",
        zIndex: 101,
        boxShadow: "0 -4px 24px rgba(140,110,60,0.15)",
        fontFamily: ff,
        animation: "su 0.3s cubic-bezier(0.25,0.46,0.45,0.94)",
      }}>
        {/* Drag handle */}
        <div style={{
          width: 40, height: 4,
          background: "rgba(140,110,60,0.2)",
          borderRadius: 2, margin: "0 auto 20px",
        }} />
        {/* Title */}
        <div style={{
          fontSize: 18, fontWeight: 700, color: "#0F172A",
          marginBottom: 16, textAlign: "center",
        }}>
          {isHi ? "आपका AI Tutor कैसे काम करता है" : "How your AI Tutor works"}
        </div>
        {/* 3 points */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {points.map((p, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%",
                background: "rgba(26,122,82,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, fontSize: 16,
              }}>
                {p.icon}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>{p.title}</div>
                <div style={{ fontSize: 12, color: "#8B7355", marginTop: 2 }}>{p.sub}</div>
              </div>
            </div>
          ))}
        </div>
        {/* Got it button */}
        <div
          className="tactile-btn-primary"
          onClick={handleDismiss}
          style={{
            marginTop: 24, width: "100%",
            background: "#1A7A52", color: "#FFFFFF",
            borderRadius: 14, padding: "14px 0",
            fontSize: 15, fontWeight: 600, fontFamily: ff,
            textAlign: "center", cursor: "pointer",
            borderBottom: "3px solid #15633F",
          }}
        >
          {isHi ? "समझ गया, चलो शुरू करें!" : "Got it, let's go!"}
        </div>
      </div>
    </>
  );
}
const trackRevisionTriggered = () => {};

// Reference diagram matcher — maps keywords to SVG components
const REF_DIAGRAMS = [
  { keys: ["quadrant","coordinate plane","cartesian","plotting point","x-axis","y-axis"], component: SvgCoordinatePlane, label: "Coordinate plane" },
  { keys: ["pythagoras","hypotenuse","right triangle","right angle triangle"], component: SvgPythagoras, label: "Pythagoras theorem" },
  { keys: ["sin ","cos ","tan ","trigonometric ratio","trigonometry"], component: SvgTrigRatios, label: "Trig ratios" },
  { keys: ["height and distance","angle of elevation","angle of depression","tower","building","shadow"], component: SvgHeightDistance, label: "Height & distance" },
  { keys: ["acute angle","obtuse angle","right angle","types of angle","reflex angle"], component: SvgAngleTypes, label: "Types of angles" },
  { keys: ["equilateral","isosceles","scalene","types of triangle"], component: SvgTriangleTypes, label: "Triangle types" },
  { keys: ["angle sum property","sum of angles","180"], component: SvgAngleSumProperty, label: "Angle sum" },
  { keys: ["similar triangle","aa similarity","sas similarity"], component: SvgSimilarTriangles, label: "Similar triangles" },
  { keys: ["basic proportionality","bpt","thales","parallel to base"], component: SvgBPT, label: "BPT" },
  { keys: ["radius","diameter","chord","arc","circle"], component: SvgCircleParts, label: "Circle parts" },
  { keys: ["tangent","point of contact"], component: SvgTangentToCircle, label: "Tangent" },
  { keys: ["distance formula","distance between two points","section formula"], component: SvgDistanceFormula, label: "Distance formula" },
  { keys: ["sector","segment","area of sector"], component: SvgSectorArea, label: "Sector & segment" },
  { keys: ["parallel lines","transversal","corresponding angle","alternate angle"], component: SvgParallelTransversal, label: "Parallel lines" },
  { keys: ["parallelogram","rectangle","rhombus","square","quadrilateral"], component: SvgQuadrilateralTypes, label: "Quadrilaterals" },
  { keys: ["reflection","mirror","incident ray","reflected ray"], component: SvgReflectionLaw, label: "Reflection" },
  { keys: ["concave mirror","focal length","principal axis","real image"], component: SvgConcaveMirror, label: "Concave mirror" },
  { keys: ["convex mirror","rear view","virtual image","diverge"], component: SvgConvexMirror, label: "Convex mirror" },
  { keys: ["refraction","snell","bending of light","denser medium","rarer medium"], component: SvgRefraction, label: "Refraction" },
  { keys: ["convex lens","converging lens","focal point"], component: SvgConvexLens, label: "Convex lens" },
  { keys: ["dispersion","prism","spectrum","vibgyor","rainbow"], component: SvgDispersion, label: "Dispersion" },
  { keys: ["human eye","cornea","retina","myopia","hypermetropia"], component: SvgHumanEye, label: "Human eye" },
  { keys: ["sound wave","compression","rarefaction","longitudinal"], component: SvgSoundWave, label: "Sound wave" },
  { keys: ["magnetic field","bar magnet","field line"], component: SvgMagneticFieldLines, label: "Magnetic field" },
  { keys: ["solenoid","electromagnet","coil"], component: SvgSolenoid, label: "Solenoid" },
  { keys: ["electric motor","fleming","force on conductor"], component: SvgElectricMotor, label: "Electric motor" },
  { keys: ["bohr","electron","shell","orbit","atomic structure","electronic configuration"], component: SvgBohrModel, label: "Bohr model" },
  { keys: ["cell structure","plant cell","animal cell","nucleus","cytoplasm","organelle"], component: SvgCellStructure, label: "Cell structure" },
  { keys: ["cone","sphere","hemisphere","frustum","curved surface area"], component: SvgConeSphere, label: "Cone & sphere" },
];

function getRefDiagram(problemText) {
  const text = (problemText || "").toLowerCase();
  for (const d of REF_DIAGRAMS) {
    if (d.keys.some(k => text.includes(k))) return d;
  }
  return null;
}

export function TutorApp({ lang, setLang, onHome, profile }) {
  const t = LANG[lang];
  const ff = lang==="hi"?"'Noto Sans Devanagari','Poppins',sans-serif":"'Poppins',sans-serif";
  const studentClass = profile?.studentClass || 10;
  const board = profile?.board || "CBSE";
  const [problem, setProblem] = useState(""); const [started, setStarted] = useState(false);
  const [messages, setMessages] = useState([]); const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [typingDone, setTypingDone] = useState(true);
  const [apiMessages, setApiMessages] = useState([]);
  const [showRevision, setShowRevision] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  const [imgData, setImgData] = useState(null);
  const [imgPreviewUrl, setImgPreviewUrl] = useState(null);
  const [showImgExpanded, setShowImgExpanded] = useState(false);
  const [progress, setProgress] = useState(null);
  const [solved, setSolved] = useState(false);
  const [pendingSolved, setPendingSolved] = useState(false);
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const [mcqOptions, setMcqOptions] = useState([]);
  const [nextQuestion, setNextQuestion] = useState(null);
  const [loadingNext, setLoadingNext] = useState(false);
  const [conceptUnderstood, setConceptUnderstood] = useState(null); // null | true | false
  const [hadHesitation, setHadHesitation] = useState(false);
  const [bannerExpanded, setBannerExpanded] = useState(false);
  const [revisionConcept, setRevisionConcept] = useState(null);
  const [refDiagramOpen, setRefDiagramOpen] = useState(false);

  // Auto-start if coming from Explain a topic with a pre-generated question
  useEffect(()=>{
    if(profile?.autoQuestion && !started) {
      setProblem(profile.autoQuestion);
      const t = setTimeout(()=>{ startSession(); }, 300);
      return ()=>clearTimeout(t);
    }
  },[]);
  const [sessionHistory, setSessionHistory] = useState([]); // last 5 problems
  const bottomRef = useRef(null); const inputRef = useRef(null);

  const handleLangChange = (l) => {
    setLang(l);
    if(started){setStarted(false);setMessages([]);setApiMessages([]);setProblem("");setInput("");setTypingDone(true);setImgData(null);setRevisionConcept(null);}
  };
  useEffect(()=>{
    // Don't auto-focus on mobile — it opens the keyboard while user is reading
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if(typingDone&&!loading&&!isMobile) inputRef.current?.focus();
  },[typingDone,loading]);
  useEffect(()=>{bottomRef.current?.scrollIntoView({behavior:"smooth"});},[messages,loading]);

  // No timer fallback — celebration only fires via [SOLVED] tag from Claude

  const lastTutorMsg = [...messages].reverse().find(m=>m.role==="tutor")?.content||"";

  // Detect NEEDS_REVISION signal and STEP progress from Claude
  const checkForRevisionSignal = (reply) => {
    // Parse step progress [STEP:X/N]
    const stepMatch = reply.match(/\[STEP:\s*(\d+)\s*\/\s*(\d+)\]/i);
    if (stepMatch) {
      setProgress({current: parseInt(stepMatch[1]), total: parseInt(stepMatch[2])});
    } else if (!progress) {
      setProgress({current: 1, total: 3});
    }
    let cleaned = reply.replace(/\[STEP:\s*\d+\s*\/\s*\d+\]/gi, "").trim();

    // Parse MCQ options [OPTIONS: A|B|C|D]
    const optMatch = cleaned.match(/\[OPTIONS:\s*([^\]]+)\]/i);
    if (optMatch) {
      const opts = optMatch[1].split("|").map(o=>o.trim()).filter(Boolean);
      setMcqOptions(opts);
      cleaned = cleaned.replace(/\[OPTIONS:[^\]]+\]/gi, "").trim();
    } else {
      setMcqOptions([]);
    }

    // Detect SOLVED tag — fire confetti now, show screen only after typing done
    if (/\[SOLVED\]/i.test(cleaned)) {
      trackChatSolved("", "", stepMatch ? parseInt(stepMatch[2]) : 0);
      setConfettiTrigger(t => t+1); // confetti drops while Claude types
      setPendingSolved(true);        // screen shows after typing finishes
      // Fetch next question in background while typing plays
      setLoadingNext(true);
      authedFetch(`${API_BASE}/api/next-question`, {
        method:"POST",
        body: JSON.stringify({problem, studentClass, board, lang})
      }).then(r=>r.json()).then(data=>{
        const q = data.content?.[0]?.text?.trim();
        if(q) setNextQuestion(q);
      }).catch(()=>{}).finally(()=>setLoadingNext(false));
      cleaned = cleaned.replace(/\[SOLVED\]/gi, "").trim();
    }

    // Detect HESITATED tag
    if (/\[HESITATED\]/i.test(cleaned)) {
      setHadHesitation(true);
      cleaned = cleaned.replace(/\[HESITATED\]/gi, "").trim();
    }

    // Detect CONCEPT_CHECK tag — strip it (options handle the UI)
    if (/\[CONCEPT_CHECK\]/i.test(cleaned)) {
      cleaned = cleaned.replace(/\[CONCEPT_CHECK\]/gi, "").trim();
    }

    // Handle NEEDS_REVISION
    const revMatch = cleaned.match(/NEEDS_REVISION:\s*(.+)/i);
    if (revMatch) {
      const concept = revMatch[1].trim().replace(/["""]/g,"");
      trackRevisionTriggered(concept);
      setRevisionConcept(concept);
      setShowRevision(true);
      cleaned = cleaned.replace(/NEEDS_REVISION:\s*.+/i, "").trim();
    }
    return cleaned;
  };

  // Auto-generate MCQ options disabled - Claude handles this via prompt
  // Auto-generation was hallucinating wrong values from the problem


  const startSession = async () => {
    if(!problem.trim()||!studentClass) return;
    setIsStarting(true);
    const profileInfo = profile.name ? `Student name: ${profile.name}, Age: ${profile.age||"unknown"}, Gender: ${profile.sex||"unknown"}, Class: ${studentClass}` : `Class: ${studentClass}`;
    const userContent = imgData
      ? [{type:"image",source:{type:"base64",media_type:imgData.mediaType,data:imgData.base64}},{type:"text",text:lang==="hi"?`${profileInfo}। यह उनके सवाल की फोटो है। सवाल: "${problem}"\nपहला मार्गदर्शक सवाल पूछें।`:`${profileInfo}. This is the problem photo. Problem: "${problem}"\nAsk the first guiding question.`}]
      : (lang==="hi"?`${profileInfo}। सवाल: "${problem}"\nपहला मार्गदर्शक सवाल पूछें।`:`${profileInfo}. Problem: "${problem}"\nAsk the first guiding question.`);
    const first = {role:"user",content:userContent};
    const api = [first]; setApiMessages(api);
    let reply = await askClaude(api, {studentClass, board, lang});
    reply = checkForRevisionSignal(reply);
    setIsStarting(false);
    setStarted(true); setLoading(false); setTypingDone(false);
    setMessages([{role:"tutor",content:reply,typing:true}]);
    setApiMessages([first,{role:"assistant",content:reply}]);

  };

  const sendMessage = async (text) => {
    const txt = text||input.trim();
    if(!txt||loading) return;

    // Intercept concept check answers — don't send to Claude
    if(pendingSolved || solved) {
      const isYes = /yes|got it|understood|हाँ|समझ/i.test(txt);
      const isNo = /no|not really|नहीं|नही/i.test(txt);
      if(isYes || isNo) {
        setConceptUnderstood(isYes);
        setMcqOptions([]);
        return;
      }
    }

    setInput("");
    const userMsg = {role:"user",content:txt};
    const api = [...apiMessages,userMsg]; setApiMessages(api);
    setMessages(p=>[...p,{role:"user",content:txt}]);
    if(api.length <= 2) trackChatQuestionAsked("","");
    setLoading(true); setTypingDone(false);
    let reply = await askClaude(api, {studentClass, board, lang});
    reply = checkForRevisionSignal(reply);
    setMessages(p=>[...p,{role:"tutor",content:reply,typing:true}]);
    setApiMessages([...api,{role:"assistant",content:reply}]);
    setLoading(false);
  };

  const reset = () => {
    // Save current problem to session history before clearing
    if(problem.trim() && started) {
      setSessionHistory(h => {
        const alreadyExists = h.some(item => item.problem === problem.trim());
        if(alreadyExists) return h;
        return [{problem, imgPreviewUrl, messages, apiMessages}, ...h].slice(0, 5);
      });
    }
    setProblem(""); setStarted(false); setMessages([]); setApiMessages([]);
    setInput(""); setTypingDone(true); setImgData(null); setImgPreviewUrl(null); setRevisionConcept(null); setProgress(null); setBannerExpanded(false); setSolved(false); setPendingSolved(false); setMcqOptions([]); setIsStarting(false); setNextQuestion(null); setConceptUnderstood(null); setHadHesitation(false); setRefDiagramOpen(false);
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div style={{height:"100vh",background:C.bg,fontFamily:ff,display:"flex",overflow:"hidden"}}>
      <div className="desktop-sidebar" style={{width:240,flexShrink:0,background:C.surface,borderRight:`1px solid ${C.border}`,display:"flex",flexDirection:"column"}}>
        <div style={{display:"flex",flexDirection:"column",height:"100%",fontFamily:ff}}>
        <div style={{padding:"16px 16px 14px",display:"flex",alignItems:"center",gap:10,borderBottom:`1px solid ${C.border}`}}>
          <SamajhIcon size={32}/>
          <span style={{fontWeight:600,fontSize:16,letterSpacing:"-0.02em",color:C.text}}> Samajh</span>
        </div>
        <div style={{padding:"12px 12px 8px"}}>
          <button onClick={()=>{reset();setSidebarOpen(false);}} style={{width:"100%",padding:"10px 14px",background:C.primaryMid,color:"white",border:"none",borderRadius:12,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:ff,display:"flex",alignItems:"center",gap:8}}>
            <span>+</span>
            <span>{lang==="hi"?"नया सवाल":"New problem"}</span>
          </button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"8px"}}>
          <div style={{fontSize:11,fontWeight:600,color:C.textMute,letterSpacing:"0.03em",padding:"6px 8px 8px"}}>
            {lang==="hi"?"हाल के सवाल":"Recent"}
          </div>
          {sessionHistory.length===0 && (
            <div style={{padding:"8px 12px",fontSize:13,color:C.textMute}}>
              {lang==="hi"?"अभी कोई सवाल नहीं":"No history yet"}
            </div>
          )}
          {sessionHistory.map((h,i)=>(
            <button key={i} onClick={()=>{reset();setProblem(h.problem);if(h.imgPreviewUrl)setImgPreviewUrl(h.imgPreviewUrl);if(h.messages&&h.messages.length>0){setMessages(h.messages);setApiMessages(h.apiMessages||[]);setStarted(true);}setSidebarOpen(false);}}
              style={{width:"100%",background:"none",border:"none",borderRadius:10,padding:"8px 10px",cursor:"pointer",fontFamily:ff,textAlign:"left",display:"flex",alignItems:"center",gap:10}}
              className="card-tactile"
            >
              {h.imgPreviewUrl
                ? <img src={h.imgPreviewUrl} alt="" style={{width:28,height:28,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
                : <div style={{width:28,height:28,borderRadius:6,background:C.primarySoft,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:12}}>Q</div>
              }
              <span style={{fontSize:13,color:C.text,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",lineHeight:1.4,flex:1}}>{h.problem}</span>
            </button>
          ))}
        </div>
        <div style={{borderTop:`1px solid ${C.border}`,padding:"12px"}}>
          <button onClick={()=>{reset();onHome();setSidebarOpen(false);}}
            style={{width:"100%",padding:"10px 14px",background:"none",border:`1px solid ${C.border}`,borderRadius:12,fontSize:14,fontWeight:600,color:C.textMid,cursor:"pointer",fontFamily:ff,marginBottom:10}}
            className="card-tactile"
          >
            {lang==="hi"?"← होम":"← Home"}
          </button>
          <div style={{display:"flex",justifyContent:"center"}}>
            <LangToggle lang={lang} setLang={handleLangChange}/>
          </div>
        </div>
      </div>
      </div>
      {sidebarOpen&&(
        <>
          <div onClick={()=>setSidebarOpen(false)} style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)",zIndex:100}}/>
          <div style={{position:"fixed",left:0,top:0,bottom:0,width:260,background:C.surface,zIndex:101,animation:"slideInLeft .22s ease",boxShadow:"4px 0 20px rgba(0,0,0,0.12)"}}>
            <div style={{display:"flex",flexDirection:"column",height:"100%",fontFamily:ff}}>
        <div style={{padding:"16px 16px 14px",display:"flex",alignItems:"center",gap:10,borderBottom:`1px solid ${C.border}`}}>
          <SamajhIcon size={32}/>
          <span style={{fontWeight:600,fontSize:16,letterSpacing:"-0.02em",color:C.text,flex:1}}>Samajh</span>
          <button onClick={()=>setSidebarOpen(false)} className="touch-target" style={{background:"none",border:"none",fontSize:18,color:C.textMute,borderRadius:6,lineHeight:1}}>x</button>
        </div>
        <div style={{padding:"12px 12px 8px"}}>
          <button onClick={()=>{reset();setSidebarOpen(false);}} style={{width:"100%",padding:"10px 14px",background:C.primaryMid,color:"white",border:"none",borderRadius:12,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:ff,display:"flex",alignItems:"center",gap:8}}>
            <span>+</span>
            <span>{lang==="hi"?"नया सवाल":"New problem"}</span>
          </button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"8px"}}>
          <div style={{fontSize:11,fontWeight:600,color:C.textMute,letterSpacing:"0.03em",padding:"6px 8px 8px"}}>
            {lang==="hi"?"हाल के सवाल":"Recent"}
          </div>
          {sessionHistory.length===0 && (
            <div style={{padding:"8px 12px",fontSize:13,color:C.textMute}}>
              {lang==="hi"?"अभी कोई सवाल नहीं":"No history yet"}
            </div>
          )}
          {sessionHistory.map((h,i)=>(
            <button key={i} onClick={()=>{reset();setProblem(h.problem);if(h.imgPreviewUrl)setImgPreviewUrl(h.imgPreviewUrl);if(h.messages&&h.messages.length>0){setMessages(h.messages);setApiMessages(h.apiMessages||[]);setStarted(true);}setSidebarOpen(false);}}
              style={{width:"100%",background:"none",border:"none",borderRadius:10,padding:"8px 10px",cursor:"pointer",fontFamily:ff,textAlign:"left",display:"flex",alignItems:"center",gap:10}}
              className="card-tactile"
            >
              {h.imgPreviewUrl
                ? <img src={h.imgPreviewUrl} alt="" style={{width:28,height:28,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
                : <div style={{width:28,height:28,borderRadius:6,background:C.primarySoft,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:12}}>Q</div>
              }
              <span style={{fontSize:13,color:C.text,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",lineHeight:1.4,flex:1}}>{h.problem}</span>
            </button>
          ))}
        </div>
        <div style={{borderTop:`1px solid ${C.border}`,padding:"12px"}}>
          <button onClick={()=>{reset();onHome();setSidebarOpen(false);}}
            style={{width:"100%",padding:"10px 14px",background:"none",border:`1px solid ${C.border}`,borderRadius:12,fontSize:14,fontWeight:600,color:C.textMid,cursor:"pointer",fontFamily:ff,marginBottom:10}}
            className="card-tactile"
          >
            {lang==="hi"?"← होम":"← Home"}
          </button>
          <div style={{display:"flex",justifyContent:"center"}}>
            <LangToggle lang={lang} setLang={handleLangChange}/>
          </div>
        </div>
      </div>
          </div>
        </>
      )}

      {/* Main content */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>

      {/* ═══ HEADER — AI-TUTOR-STEP-2-APPLIED: minimal Home pill only ═══ */}
      {!started ? (
        <div className="animate-in stagger-0" style={{
          background:"#5AAA9A",
          padding:"12px 16px 4px", display:"flex", alignItems:"center", justifyContent:"flex-start",
          flexShrink:0,
        }}>
          <div
            className="tactile-pill"
            onClick={()=>{onHome();}}
            style={{
              minHeight:44, display:"inline-flex", alignItems:"center", gap:4,
              fontSize:13, fontWeight:600, color:"#FFFFFF",
              background:"rgba(255,255,255,0.2)", borderRadius:8,
              padding:"6px 14px", cursor:"pointer", fontFamily:ff,
            }}
          >
            {lang==="hi"?"← होम":"← Home"}
          </div>
        </div>
      ) : (
        <div className="tutor-topbar" style={{background:"white",borderBottom:"1px solid rgba(140,110,60,0.08)",padding:"10px 14px",display:"flex",alignItems:"center",gap:10,position:"sticky",top:0,zIndex:50,boxShadow:"0 2px 8px rgba(140,110,60,0.08), 0 1px 2px rgba(140,110,60,0.06)"}}>
          {/* Back arrow */}
          <button className="touch-target btn-secondary-tactile" onClick={()=>{reset();onHome();setSidebarOpen(false);}} style={{background:"rgba(140,110,60,0.05)",border:"none",borderRadius:10,flexShrink:0}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3A5A4A" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          {/* Truncated problem */}
          <div style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:13,fontWeight:600,color:"#1A2A3A",fontFamily:ff}}>
            {problem ? problem.slice(0,50) + (problem.length > 50 ? "..." : "") : (lang==="hi"?"Samajh AI":"Samajh AI")}
          </div>
          {/* New + Home */}
          <div style={{display:"flex",gap:6,flexShrink:0}}>
            <button className="touch-target btn-secondary-tactile" onClick={reset} style={{background:"rgba(26,122,82,0.08)",border:"none",borderRadius:8,padding:"7px 12px",fontSize:12,color:"#1A7A52",fontWeight:600,fontFamily:ff,whiteSpace:"nowrap"}}>
              + {lang==="hi"?"नया":"New"}
            </button>
            <button className="touch-target btn-secondary-tactile" onClick={()=>{reset();onHome();setSidebarOpen(false);}} style={{background:"rgba(140,110,60,0.05)",border:"none",borderRadius:10}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5A6A60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* ═══ LANDING SCREEN — AI-TUTOR-STEP-3,4,5,6,7,8-APPLIED ═══ */}
      {!started&&(
        <div style={{
          flex:1, overflowY:"auto", padding:"4px 16px 32px",
          background:"#5AAA9A",
          fontFamily:ff,
        }}>

          {/* AI-TUTOR-STEP-4-APPLIED: first-visit Socratic bottom sheet overlay */}
          <SocraticOverlay lang={lang} ff={ff} />

          {/* AI-TUTOR-STEP-3-APPLIED: headline + Socratic subtitle */}
          <div className="animate-in stagger-1" style={{textAlign:"center",marginBottom:20,marginTop:8}}>
            <div style={{fontSize:24,fontWeight:700,color:"#FFFFFF",letterSpacing:"-0.02em",lineHeight:1.2,marginBottom:8}}>
              {lang==="hi"?"किस सवाल में मदद चाहिए?":"What do you need help with?"}
            </div>
            <div style={{fontSize:14,fontWeight:500,color:"#F0FDF4",lineHeight:1.5,padding:"0 8px"}}>
              {lang==="hi"
                ? "कुछ भी पूछो — मैं step-by-step guide करूँगा, सीधे जवाब नहीं दूँगा"
                : "Ask me anything — I'll guide you step by step, not just give the answer"}
            </div>
          </div>

          {/* AI-TUTOR-STEP-5-APPLIED: Sample questions with tappable affordance + arrow */}
          <div className="animate-scale-in stagger-2" style={{background:"rgba(255,255,255,0.93)",borderRadius:16,padding:`16px`,marginBottom:16,border:"1px solid rgba(140,110,60,0.06)",boxShadow:"0 2px 8px rgba(140,110,60,0.08), 0 1px 2px rgba(140,110,60,0.06)"}}>
            <div style={{fontSize:11,fontWeight:600,color:"#7A7A6A",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:12}}>
              {lang==="hi"?"सैंपल सवाल":"Sample questions"}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {(profile?.explainTopic
              ? [`Explain ${profile.explainTopic} with an example`,`Give a numerical problem on ${profile.explainTopic}`,`What is the formula for ${profile.explainTopic}?`]
              : (t.examplesByClass?.[studentClass]||t.examples)
            ).map((ex,i)=>{
              const subjColor = ["#F59E0B","#10B981","#8B5CF6"][i%3];
              return (
                <div key={i} className="tactile-card" onClick={()=>setProblem(ex)} style={{
                  background:"#FFFFFF",
                  borderRadius:12, padding:"14px 16px",
                  borderLeft:`3px solid ${subjColor}`,
                  boxShadow:"0 2px 10px rgba(140,110,60,0.12)",
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                  cursor:"pointer",
                }}>
                  <span style={{fontSize:14,fontWeight:500,color:"#3D2E15",lineHeight:1.4,flex:1}}>{ex}</span>
                  <span style={{fontSize:14,color:"#8B7355",marginLeft:12,flexShrink:0}}>→</span>
                </div>
              );
            })}
            </div>
          </div>

          {/* AI-TUTOR-STEP-6-APPLIED: Input area — subtle border to signal input */}
          <div className="animate-in stagger-3" style={{
            background:"#FFFFFF", borderRadius:14, padding:"14px 16px",
            marginBottom:16,
            border:"1.5px solid rgba(15,42,74,0.12)",
            boxShadow:"0 2px 8px rgba(140,110,60,0.08)",
          }}>
            <textarea value={problem} onChange={e=>setProblem(e.target.value)}
              placeholder={t.placeholderByClass?.[studentClass]||t.placeholder} rows={3}
              className="tutor-input"
              style={{
                width:"100%", background:"transparent",
                border:"none", outline:"none", resize:"none",
                fontSize:14, fontWeight:400, color:"#3D2E15",
                lineHeight:1.5, fontFamily:ff,
                boxSizing:"border-box",
              }}
              onKeyDown={e=>e.key==="Enter"&&e.metaKey&&startSession()}
            />
            <style>{`.tutor-input::placeholder{color:#8B7355;opacity:1}`}</style>
          </div>

          {/* AI-TUTOR-STEP-7-APPLIED: CTA — no arrow, proper disabled state */}
          <div className="animate-in stagger-4" style={{display:"flex",gap:10,marginBottom:24}}>
            <div
              className="tactile-btn-primary"
              onClick={problem.trim() ? startSession : undefined}
              style={{
                flex:1,
                background: problem.trim() ? "#1A7A52" : "rgba(26,122,82,0.4)",
                color:"#FFFFFF", borderRadius:14,
                padding:"14px 0", fontSize:15, fontWeight:600,
                fontFamily:ff, textAlign:"center",
                cursor: problem.trim() ? "pointer" : "default",
                borderBottom: problem.trim() ? "3px solid #15633F" : "3px solid transparent",
                pointerEvents: problem.trim() ? "auto" : "none",
                transition:"background 0.15s ease",
              }}
            >
              {isStarting ? (lang==="hi"?"सोच रहा हूँ...":"Thinking...") : (lang==="hi"?"चलो हल करें":"Let's solve this")}
            </div>
            <div
              className="tactile-btn-primary"
              onClick={()=>setShowPhoto(true)}
              style={{
                display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                background:"#FFFFFF", color:"#1A7A52",
                borderRadius:14, padding:"14px 20px",
                fontSize:14, fontWeight:600, fontFamily:ff,
                border:"1.5px solid rgba(26,122,82,0.3)",
                borderBottom:"3px solid rgba(26,122,82,0.15)",
                cursor:"pointer",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M5.5 3L4.5 4.5H2.5A1.5 1.5 0 001 6v6.5A1.5 1.5 0 002.5 14h11a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5h-2L10.5 3h-5z" stroke="currentColor" strokeWidth="1.2"/><circle cx="8" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.2"/></svg>
              {lang==="hi"?"फोटो":"Photo"}
            </div>
          </div>

          {/* Recent history */}
          {sessionHistory.length>0&&(
            <div style={{marginTop:4}}>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",marginBottom:10,fontWeight:600,letterSpacing:"0.03em",textTransform:"uppercase"}}>
                {lang==="hi"?"हाल के सवाल":"Recent"}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {sessionHistory.map((h,i)=>(
                  <button key={i} onClick={()=>{
                    setProblem(h.problem);
                    if(h.imgPreviewUrl) setImgPreviewUrl(h.imgPreviewUrl);
                    if(h.messages&&h.messages.length>0){setMessages(h.messages);setApiMessages(h.apiMessages||[]);setStarted(true);}
                  }} style={{
                    background:"rgba(255,255,255,0.93)",border:"1px solid rgba(140,110,60,0.06)",borderRadius:14,
                    padding:"10px 14px",cursor:"pointer",fontFamily:ff,
                    textAlign:"left",display:"flex",alignItems:"center",gap:10,
                    boxShadow:"0 2px 8px rgba(140,110,60,0.08), 0 1px 2px rgba(140,110,60,0.06)",
                  }}>
                    {h.imgPreviewUrl
                      ? <img src={h.imgPreviewUrl} alt="" style={{width:32,height:32,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
                      : <div style={{width:32,height:32,borderRadius:6,background:"rgba(26,122,82,0.06)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:"#1A7A52",fontWeight:600}}>Q</div>
                    }
                    <span style={{fontSize:13,color:"#1A2A3A",flex:1,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:1,WebkitBoxOrient:"vertical"}}>{h.problem}</span>
                    <span style={{fontSize:11,color:"#7A7A6A",flexShrink:0}}>{h.messages?.length>0?"↩ resume":"↩"}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>
      )}

      {/* Cold start loading screen */}
      {isStarting&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(255,255,255,0.96)",zIndex:200,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:ff}}>
          <div style={{width:56,height:56,borderRadius:16,background:C.primaryMid,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:20}}>
            <SamajhIcon size={56}/>
          </div>
          <div style={{fontSize:18,fontWeight:600,color:C.text,marginBottom:8}}>
            {lang==="hi"?"सोच रहा हूँ...":"Thinking..."}
          </div>
          <div style={{fontSize:13,color:C.textMute,marginBottom:28,textAlign:"center",maxWidth:260,lineHeight:1.6}}>
            {lang==="hi"
              ?"पहली बार थोड़ा समय लग सकता है।\nबस एक पल..."
              :"This may take a moment on first load.\nHang tight!"}
          </div>
          <div style={{display:"flex",gap:6}}>
            {[0,1,2].map(i=>(
              <div key={i} style={{
                width:10,height:10,borderRadius:"50%",
                background:C.primary,
                animation:`td 1.2s ease-in-out ${i*0.2}s infinite`,
              }}/>
            ))}
          </div>
        </div>
      )}
      {started&&(<>
        <div className="problem-banner" style={{background:"#f8fafc",borderBottom:`1px solid ${C.border}`,padding:"8px 14px",display:"flex",flexDirection:"column",gap:6,position:"sticky",top:0,zIndex:40}}>
          <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
            <div style={{flex:1,minWidth:0,cursor:"pointer"}} onClick={()=>setBannerExpanded((e)=>!e)}>
              <span style={{fontSize:10,fontWeight:700,color:C.textMute,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:1}}>{t.problemLabel}</span>
              <span style={{
                fontSize:13,color:C.text,lineHeight:1.5,fontWeight:500,
                display:"-webkit-box",
                WebkitLineClamp:bannerExpanded?999:2,
                WebkitBoxOrient:"vertical",
                overflow:"hidden",
              }}>{problem}</span>
              {problem.length > 80 && (
                <span style={{fontSize:11,color:C.primary,fontWeight:600,display:"block",marginTop:2}}>
                  {bannerExpanded
                    ? (lang==="hi"?"▲ कम करें":"▲ Show less")
                    : (lang==="hi"?"▼ पूरा देखें":"▼ See full question")}
                </span>
              )}
            </div>
            <button onClick={()=>{setRevisionConcept(null);setShowRevision(true);}} style={{background:C.primaryMid,border:"none",borderRadius:10,padding:"6px 12px",fontSize:12,color:"#fff",cursor:"pointer",fontWeight:600,fontFamily:ff,whiteSpace:"nowrap",flexShrink:0}}>{t.reviseBtn}</button>
          </div>
          {progress&&(
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:11,color:C.textMute,fontWeight:600,whiteSpace:"nowrap",flexShrink:0}}>
                {progress.current===progress.total
                  ? (lang==="hi"?"✓ हल":"✓ Done")
                  : (lang==="hi"?`चरण ${progress.current}/${progress.total}`:`Step ${progress.current} of ${progress.total}`)}
              </span>
              <div style={{display:"flex",gap:4,flex:1}}>
                {Array.from({length:progress.total}).map((_,i)=>(
                  <div key={i} style={{
                    flex:1,height:8,borderRadius:4,
                    background: i<progress.current
                      ? (progress.current===progress.total?C.primaryMid:C.primaryMid)
                      : C.border,
                    transition:"background .4s ease",
                  }}/>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Reference diagram — auto-matched from problem keywords */}
        {(() => {
          const ref = getRefDiagram(problem);
          if (!ref) return null;
          const DiagramComp = ref.component;
          return (
            <div style={{borderBottom:`1px solid ${C.border}`,background:"white"}}>
              <div onClick={()=>setRefDiagramOpen(o=>!o)} style={{padding:"8px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke={C.primaryMid} strokeWidth="1.5"/><circle cx="12" cy="10" r="3" stroke={C.primaryMid} strokeWidth="1.5"/><path d="M6 18l3-3 2 2 4-5 3 4" stroke={C.primaryMid} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span style={{fontSize:12,fontWeight:600,color:C.primaryMid,fontFamily:ff}}>{ref.label}</span>
                  <span style={{fontSize:10,color:C.textMute,fontFamily:ff}}>{refDiagramOpen ? "" : (lang==="hi"?"— टैप करें":"— tap to view")}</span>
                </div>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{transform:refDiagramOpen?"rotate(180deg)":"rotate(0)",transition:"transform .2s"}}>
                  <path d="M3 5l3 3 3-3" stroke={C.textMute} strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              {refDiagramOpen && (
                <div style={{padding:"0 14px 14px",maxWidth:420,margin:"0 auto"}}>
                  <div style={{background:"#E1F5EE",borderRadius:12,padding:"12px 8px",border:`0.5px solid ${C.primaryLight}`}}>
                    <DiagramComp />
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {showImgExpanded&&imgPreviewUrl&&(
          <div onClick={()=>setShowImgExpanded(false)} style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.9)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <img src={imgPreviewUrl} alt="problem" style={{maxWidth:"100%",maxHeight:"90vh",borderRadius:12,objectFit:"contain"}}/>
            <button onClick={()=>setShowImgExpanded(false)} style={{position:"absolute",top:20,right:20,background:"rgba(255,255,255,0.15)",border:"none",borderRadius:20,width:40,height:40,color:"#fff",fontSize:20,cursor:"pointer"}}>✕</button>
          </div>
        )}

        {/* Concept understood — YES screen */}
        {conceptUnderstood===true&&(
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(15,23,42,0.6)",zIndex:150,display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:ff}}>
            <div style={{background:"linear-gradient(145deg,#fffbeb,#fef3c7)",borderRadius:20,padding:"32px 24px",maxWidth:340,width:"100%",textAlign:"center",border:"1px solid #fde68a",animation:"fadeUp .4s ease"}}>
              <div style={{fontSize:48,marginBottom:12}}>🏆</div>
              <div style={{fontSize:21,fontWeight:600,color:"#92400e",letterSpacing:"-0.02em",marginBottom:8}}>
                {lang==="hi"?"शानदार! अवधारणा समझ आ गई!":"Nailed it! Concept understood!"}
              </div>
              <div style={{fontSize:13,color:"#b45309",marginBottom:20,lineHeight:1.6}}>
                {lang==="hi"?"आप आगे के सवालों के लिए तैयार हैं।":"You're ready for harder problems."}
              </div>
              {(nextQuestion||loadingNext)&&(
                <div style={{background:"rgba(255,255,255,0.7)",border:"1px solid #fde68a",borderRadius:12,padding:"12px 14px",marginBottom:16,textAlign:"left"}}>
                  <div style={{fontSize:15,color:"#92400e",fontWeight:600,marginBottom:6}}>
                    {lang==="hi"?"एक कठिन सवाल आज़माएं?":"Ready for a harder one?"}
                  </div>
                  {loadingNext
                    ? <div style={{fontSize:13,color:"#b45309",opacity:0.6}}>{lang==="hi"?"तैयार हो रहा है...":"Loading your next challenge..."}</div>
                    : <div style={{fontSize:13,color:"#92400e",lineHeight:1.5,marginBottom:10}}>{nextQuestion}</div>
                  }
                  {nextQuestion&&(
                    <button onClick={()=>{setConceptUnderstood(null);reset();setProblem(nextQuestion);}} style={{width:"100%",padding:"9px",background:C.amberDark,color:"white",border:"none",borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:ff}}>
                      {lang==="hi"?"हाँ, यह सवाल हल करें →":"Yes, try this one →"}
                    </button>
                  )}
                </div>
              )}
              <button onClick={()=>{setConceptUnderstood(null);reset();}} style={{width:"100%",padding:"12px",background:"white",border:"1.5px solid #fde68a",color:"#92400e",borderRadius:14,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:ff,marginBottom:8}}>
                {lang==="hi"?"अलग सवाल हल करें":"Try a different problem"}
              </button>
              <button onClick={()=>setConceptUnderstood(null)} style={{width:"100%",padding:"10px",background:"none",color:"#b45309",border:"none",fontSize:13,cursor:"pointer",fontFamily:ff}}>
                {lang==="hi"?"बातचीत देखें":"Review conversation"}
              </button>
            </div>
          </div>
        )}

        {/* Concept not understood — NO screen */}
        {conceptUnderstood===false&&(
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(15,23,42,0.7)",zIndex:150,display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:ff}}>
            <div style={{background:"#fff",borderRadius:24,padding:"32px 24px",maxWidth:340,width:"100%",textAlign:"center",boxShadow:"0 20px 60px rgba(0,0,0,0.15)",border:`1px solid ${C.border}`,animation:"fadeUp .4s ease"}}>
              <div style={{fontSize:48,marginBottom:12}}>💪</div>
              <div style={{fontSize:21,fontWeight:600,color:C.text,letterSpacing:"-0.02em",marginBottom:8}}>
                {lang==="hi"?"कोई बात नहीं — हम पहुँच रहे हैं!":"We're getting there!"}
              </div>
              <div style={{fontSize:13,color:C.textMid,marginBottom:20,lineHeight:1.6}}>
                {lang==="hi"?"एक और ऐसा सवाल करते हैं — अवधारणा और पक्की हो जाएगी।":"Let's try a similar one — the concept will click with a bit more practice."}
              </div>
              {nextQuestion&&(
                <div style={{background:C.primarySoft,border:`1px solid ${C.primaryLight}`,borderRadius:12,padding:"12px 14px",marginBottom:16,textAlign:"left"}}>
                  <div style={{fontSize:13,color:C.text,lineHeight:1.5,marginBottom:10}}>{nextQuestion}</div>
                  <button onClick={()=>{setConceptUnderstood(null);reset();setProblem(nextQuestion);}} style={{width:"100%",padding:"9px",background:C.primaryMid,color:"white",border:"none",borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:ff}}>
                    {lang==="hi"?"यह सवाल हल करें →":"Try this one →"}
                  </button>
                </div>
              )}
              <button onClick={()=>setConceptUnderstood(null)} style={{width:"100%",padding:"10px",background:"none",color:C.textMute,border:"none",fontSize:13,cursor:"pointer",fontFamily:ff}}>
                {lang==="hi"?"बातचीत देखें":"Review conversation"}
              </button>
            </div>
          </div>
        )}

        {/* Completion screen */}
        {solved&&(
          <>
          <Confetti trigger={confettiTrigger}/>
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(15,23,42,0.6)",zIndex:150,display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:ff}}
            onClick={(e)=>e.target===e.currentTarget&&setSolved(false)}
          >
            <div style={{background:"linear-gradient(145deg,#fffbeb,#fef3c7)",borderRadius:20,padding:"32px 24px",maxWidth:340,width:"100%",textAlign:"center",border:"1px solid #fde68a",animation:"fadeUp .4s ease",position:"relative",zIndex:151}}>
              <div style={{fontSize:48,marginBottom:12}}>🎯</div>
              <div style={{fontSize:22,fontWeight:600,color:"#92400e",letterSpacing:"-0.02em",marginBottom:8,lineHeight:1.2}}>
                {lang==="hi"?"शानदार! सवाल हल कर लिया!":"Brilliant! You solved it!"}
              </div>
              <div style={{fontSize:13,color:"#b45309",lineHeight:1.6,marginBottom:20}}>
                {lang==="hi"
                  ?`${progress?.total||""} चरणों में खुद सोचकर हल किया।`
                  :`${progress?.total||"All"} steps, your own thinking.`}
              </div>

              {hadHesitation&&(
                <div style={{background:"rgba(255,255,255,0.6)",border:"1px solid #fde68a",borderRadius:12,padding:"12px 14px",marginBottom:16,textAlign:"left"}}>
                  <div style={{fontSize:13,color:"#92400e",lineHeight:1.5}}>
                    {lang==="hi"
                      ?"इस सवाल में थोड़ी मुश्किल हुई। एक और ऐसा सवाल करने से अवधारणा और पक्की हो जाएगी।"
                      :"This one took a few tries. A similar question will help the concept stick."}
                  </div>
                </div>
              )}

              <div style={{background:"rgba(255,255,255,0.7)",border:"1px solid #fde68a",borderRadius:14,padding:"14px 16px",marginBottom:12,textAlign:"left"}}>
                <div style={{fontSize:14,color:"#92400e",fontWeight:600,marginBottom:12,lineHeight:1.4}}>
                  {lang==="hi"?"जवाब मिल गया — लेकिन अवधारणा समझ आई?":"You got it — but did the concept click?"}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <button onClick={()=>{setSolved(false);setConceptUnderstood(true);}} style={{
                    padding:"10px",background:C.amberDark,
                    color:"white",border:"none",borderRadius:10,fontSize:14,fontWeight:600,
                    cursor:"pointer",fontFamily:ff,
                  }}>
                    {lang==="hi"?"हाँ, समझ आई!":"Yes, got it!"}
                  </button>
                  <button onClick={()=>{setSolved(false);setConceptUnderstood(false);}} style={{
                    padding:"10px",background:"white",
                    color:"#92400e",border:"1.5px solid #fde68a",borderRadius:10,fontSize:14,fontWeight:600,
                    cursor:"pointer",fontFamily:ff,
                  }}>
                    {lang==="hi"?"नहीं, उतना नहीं":"Not really"}
                  </button>
                </div>
              </div>

              <button onClick={()=>setSolved(false)} style={{
                width:"100%",padding:"10px",background:"none",
                color:"#b45309",border:"none",fontSize:13,
                cursor:"pointer",fontFamily:ff,
              }}>
                {lang==="hi"?"बातचीत देखें":"Review conversation"}
              </button>
            </div>
          </div>
          </>
        )}
        {imgPreviewUrl&&(
          <div
            onClick={()=>setShowImgExpanded(true)}
            style={{
              position:"sticky",top:72,zIndex:39,
              background:"#fff",borderBottom:`1px solid ${C.border}`,
              cursor:"pointer",flexShrink:0,
              display:"flex",alignItems:"center",justifyContent:"center",
            }}
          >
            <img
              src={imgPreviewUrl} alt="problem figure"
              style={{width:"100%",maxHeight:160,objectFit:"contain",display:"block"}}
            />
            <div style={{
              position:"absolute",bottom:4,right:8,
              fontSize:10,color:C.textMute,
              background:"rgba(255,255,255,0.85)",
              borderRadius:6,padding:"2px 6px",
            }}>
              {lang==="hi"?"बड़ा देखें ↗":"Expand ↗"}
            </div>
          </div>
        )}

        <div className="chat-area" style={{flex:1,overflowY:"auto",padding:"16px 12px",display:"flex",flexDirection:"column",gap:14,maxWidth:760,width:"100%",margin:"0 auto",alignSelf:"center"}}>

  

          {messages.map((msg,idx)=>{
            const isTutor=msg.role==="tutor"; const isLast=idx===messages.length-1;

            // Revision summary bubble
            if(msg.role==="revision") return (
              <div key={idx} style={{animation:"fadeUp .2s ease"}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                  <div style={{height:1,flex:1,background:C.primaryLight}}/>
                  <span style={{fontSize:11,color:C.primary,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>
                    📖 {lang==="hi"?"दोहराव सारांश":"Revision Summary"}
                  </span>
                  <div style={{height:1,flex:1,background:C.primaryLight}}/>
                </div>
                <div style={{background:C.primarySoft,border:`1px solid ${C.primaryLight}`,borderRadius:14,padding:"14px 16px",fontSize:13,color:C.text,lineHeight:1.7,fontFamily:ff}}>
                  <RevisionText text={msg.content} onDone={()=>{}}/>
                </div>
              </div>
            );
            return (
              <div key={idx} className="chat-bubble" style={{display:"flex",flexDirection:isTutor?"row":"row-reverse",alignItems:"flex-end",gap:10,animation:"fadeUp .2s ease"}}>
                {isTutor&&<div style={{width:32,height:32,borderRadius:"50%",background:C.primarySoft,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{fontSize:13,fontWeight:600,color:C.primaryMuted}}>S</span>
                </div>}
                {/* CHAT-VISUAL-FIX-1-APPLIED + AI-BUBBLE-DEFINITION-BOOST: AI white + warm border + stronger shadow; student green tint only */}
                <div style={{maxWidth:"85%",background:isTutor?"#FFFFFF":"rgba(26,122,82,0.08)",color:isTutor?C.text:C.userBubbleText,borderRadius:isTutor?"16px 16px 16px 4px":"16px 16px 4px 16px",padding:"12px 16px",fontSize:15,lineHeight:1.7,border:isTutor?"1px solid rgba(140,110,60,0.10)":"none",boxShadow:isTutor?"0 2px 8px rgba(140,110,60,0.12)":"none",fontFamily:ff}}>
                  {isTutor&&isLast&&msg.typing
                    ?<TypingBubble text={msg.content} onDone={()=>{
                        setTypingDone(true);
                        setMessages(p=>p.map((m,i)=>i===idx?{...m,typing:false}:m));
                        if(pendingSolved){
                          setTimeout(()=>{setSolved(true);setPendingSolved(false);}, 1500);
                        }
                      }}/>
                    :<>{
                      /* Inline "Correct!" badge */
                      isTutor && /^(Correct|Right|Exactly|Perfect|Well done|Spot on|That's right|सही|बिल्कुल|शाबाश)/i.test(msg.content)
                        ? <><span style={{display:"inline-flex",alignItems:"center",gap:5,background:C.primarySoft,borderRadius:8,padding:"3px 10px",fontSize:12,fontWeight:600,color:C.primaryMid,marginBottom:8,marginRight:4}}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke={C.primaryMid} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            {/^(सही|बिल्कुल|शाबाश)/.test(msg.content)?"सही!":"Correct!"}
                          </span><br/><StyledTutorText text={msg.content.replace(/^(Correct!?|Right!?|Exactly!?|Perfect!?|Well done!?|Spot on!?|That's right!?|सही!?|बिल्कुल!?|शाबाश!?)\s*/i,"")}/></>
                        : isTutor
                          ? <StyledTutorText text={msg.content}/>
                          : <span style={{whiteSpace:"pre-wrap"}}>{msg.content}</span>
                    }</>}
                  {/* MCQ options — teal-accented pill buttons */}
                  {isTutor&&isLast&&typingDone&&!loading&&mcqOptions.length>0&&(
                    <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:8}}>
                      {mcqOptions.map((opt,i)=>(
                        <button key={i} onClick={()=>{
                          if(opt==="Yes, got it!" || opt==="Yes, समझ गया!") {
                            setConceptUnderstood(true);
                            setMcqOptions([]);
                            return;
                          }
                          if(opt==="Not really" || opt==="नहीं, फिर से") {
                            setConceptUnderstood(false);
                            setMcqOptions([]);
                            return;
                          }
                          setMcqOptions([]);sendMessage(opt);
                        }} style={{
                          background:"white",border:"2px solid #E2D8C8",
                          borderRadius:12,padding:"14px 20px",
                          boxShadow:"0 2px 0 #E2D8C8",
                          fontSize:14,color:"#1A2A3A",cursor:"pointer",
                          fontFamily:ff,fontWeight:600,
                          textAlign:"center",transition:"all .12s",
                          lineHeight:1.3,
                        }}
                          onMouseOver={(e)=>{e.currentTarget.style.borderColor="#1A7A52";e.currentTarget.style.background="#E8F5EE";e.currentTarget.style.boxShadow="0 2px 0 #1A7A52";}}
                          onMouseOut={(e)=>{e.currentTarget.style.borderColor="#E2D8C8";e.currentTarget.style.background="white";e.currentTarget.style.boxShadow="0 2px 0 #E2D8C8";}}
                        >{opt}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {/* CHAT-VISUAL-FIX-2-APPLIED: Typing indicator with animated bouncing dots */}
          {loading&&<div className="chat-message-enter" style={{display:"flex",gap:10,alignItems:"flex-end"}}>
            <div style={{width:32,height:32,borderRadius:"50%",background:C.primarySoft,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <span style={{fontSize:13,fontWeight:600,color:C.primaryMuted}}>S</span>
            </div>
            <div style={{background:"#FFFFFF",borderRadius:"16px 16px 16px 4px",padding:"14px 20px",border:"1px solid rgba(140,110,60,0.10)",boxShadow:"0 2px 8px rgba(140,110,60,0.12)",display:"flex",alignItems:"center",gap:5}}>
              <span className="typing-dot" style={{animationDelay:"0ms"}}/>
              <span className="typing-dot" style={{animationDelay:"150ms"}}/>
              <span className="typing-dot" style={{animationDelay:"300ms"}}/>
            </div>
          </div>}
          <div ref={bottomRef}/>
        </div>

        {/* Reopen celebration pill — shows after Review conversation */}
        {!solved&&nextQuestion&&typingDone&&(
          <div style={{padding:"8px 14px 0",display:"flex",justifyContent:"center"}}>
            <button onClick={()=>setSolved(true)} style={{
              background:"linear-gradient(135deg,#fffbeb,#fef3c7)",
              border:"1px solid #fde68a",borderRadius:20,
              padding:"7px 16px",fontSize:13,color:"#92400e",
              cursor:"pointer",fontFamily:ff,fontWeight:600,
              display:"flex",alignItems:"center",gap:6,
            }}>
              🎉 {lang==="hi"?"नतीजा देखें":"See result & next question"}
            </button>
          </div>
        )}

        {/* Copy solution button — shows when problem is solved */}
        {(nextQuestion||pendingSolved)&&typingDone&&(
          <div style={{padding:"8px 14px 0",display:"flex",justifyContent:"center"}}>
            <button onClick={()=>{
              const solution = messages.filter(m=>m.role==="tutor").map(m=>m.content).join("\n\n");
              navigator.clipboard.writeText(solution).then(()=>{
                const btn = document.getElementById("copy-solution-btn");
                if(btn){btn.textContent=lang==="hi"?"✓ कॉपी हो गया":"✓ Copied!";setTimeout(()=>{btn.textContent=lang==="hi"?"📋 हल कॉपी करें":"📋 Copy solution";},2000);}
              });
            }} id="copy-solution-btn" style={{
              background:"white",border:"none",borderRadius:20,
              padding:"7px 16px",fontSize:13,color:C.primaryMid,
              cursor:"pointer",fontFamily:ff,fontWeight:500,
              display:"flex",alignItems:"center",gap:6,
            }}>
              {lang==="hi"?"📋 हल कॉपी करें":"📋 Copy solution"}
            </button>
          </div>
        )}

        {/* Session feedback — thumbs up/down */}
        {(pendingSolved||solved)&&typingDone&&(
          <SessionFeedback topic={problem} lang={lang} />
        )}

        {typingDone&&!loading&&(
          <div className="hints-row" style={{padding:"0 14px 10px",display:"flex",gap:7,flexWrap:"wrap"}}>
            {(()=>{
              const lastMsg = [...messages].reverse().find(m=>m.role==="tutor");
              const theoryMatch = lastMsg?.content?.match(/📌\s*(?:Theory|Concept|सिद्धांत|अवधारणा)\s*:\s*([^\n]+)/i);
              const theory = theoryMatch?.[1]?.trim();
              return theory ? (
                <button
                  key="revise-concept"
                  onClick={()=>{setRevisionConcept(theory);setShowRevision(true);}}
                  style={{background:"white",border:"1px solid rgba(140,110,60,0.12)",borderRadius:20,padding:"8px 16px",fontSize:13,color:"#1A7A52",cursor:"pointer",fontFamily:ff,fontWeight:600,transition:"all .12s"}}
                  onMouseOver={(e)=>{e.currentTarget.style.borderColor="#1A7A52";}}
                  onMouseOut={(e)=>{e.currentTarget.style.borderColor="rgba(140,110,60,0.12)";}}
                >
                  📖 {lang==="hi"?"यह अवधारणा दोहराएं":"Revise this concept"}
                </button>
              ) : null;
            })()}
            {/* CHAT-VISUAL-FIX-4-APPLIED: quick-action pills with icons + tactile-pill */}
            {!solved && t.hints.map((h, i) => {
              const icon = ["😕", "✏️", "💡"][i] || "";
              return (
                <button key={h} className="tactile-pill" onClick={()=>sendMessage(h)} style={{
                  display:"inline-flex", alignItems:"center", gap:5,
                  background:"#FFFFFF", border:"1px solid rgba(140,110,60,0.15)",
                  borderRadius:20, padding:"8px 14px",
                  fontSize:13, fontWeight:500, color:"#615030",
                  cursor:"pointer", fontFamily:ff, minHeight:44,
                  boxShadow:"0 1px 4px rgba(140,110,60,0.06)",
                }}>
                  <span>{icon}</span> {h}
                </button>
              );
            })}
          </div>
        )}

        <div className="input-bar" style={{background:"white",borderTop:"1px solid rgba(140,110,60,0.08)",padding:"10px 16px 20px"}}>
          <div style={{display:"flex",gap:10,alignItems:"flex-end",background:"white",border:"1.5px solid rgba(140,110,60,0.10)",borderRadius:16,padding:"4px 4px 4px 16px"}}>
            <textarea ref={inputRef} value={input} onChange={(e)=>setInput(e.target.value)}
              onKeyDown={(e)=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage();}}}
              placeholder={t.inputPlaceholder} rows={1} disabled={loading||!typingDone}
              style={{flex:1,background:"transparent",border:"none",fontSize:15,color:"#0A1820",resize:"none",lineHeight:1.5,fontFamily:ff,padding:"10px 0",opacity:(loading||!typingDone)?0.5:1,maxHeight:120,minHeight:28}}/>
            <button className="btn-secondary-tactile" onClick={()=>sendMessage()} disabled={!input.trim()||loading||!typingDone} style={{width:44,height:44,background:input.trim()&&typingDone?"linear-gradient(135deg, #1A7A52, #228B5A)":"#D0C8B8",border:"none",borderRadius:12,cursor:input.trim()&&typingDone?"pointer":"not-allowed",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .2s",boxShadow:input.trim()&&typingDone?"0 2px 8px rgba(26,122,82,0.25)":"none"}}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>
      </>)}

      {showRevision&&<RevisionPanel
        problem={problem}
        lastTutorMsg={lastTutorMsg}
        onClose={(summary)=>{
          setShowRevision(false);
          setRevisionConcept(null);
          if(summary && typeof summary === "string" && summary.length > 10) {
            setMessages(p=>[...p,{role:"revision",content:summary,concept:summary.split("\n")[0]}]);
          }
        }}
        lang={lang}
        studentClass={studentClass||10}
        board={board}
        autoConceptName={revisionConcept}
      />}
      {showPhoto&&<PhotoUpload onExtracted={(text,img,previewUrl)=>{setProblem(text);setImgData(img);setImgPreviewUrl(previewUrl);setShowPhoto(false);}} onCancel={()=>setShowPhoto(false)} lang={lang}/>}
      </div>
    </div>
  );
}
